import { geoAlbers, geoMercator, geoPath } from 'd3-geo';
import React, { useMemo } from 'react';
import statesData from './data/states.json';
import { StateOutlineMapProps, StatesData } from './types';

const StateOutlineMap: React.FC<StateOutlineMapProps> = ({
  stateName = 'California',
  width = 400,
  height = 400,
  strokeClassName = 'stroke-black',
  fillClassName = 'fill-white',
  strokeWidth = 1,
  opacity = 0.8,
}) => {
  const typedStatesData = statesData as StatesData;

  const stateFeature = useMemo(() => {
    if (!typedStatesData?.features) return null;
    return typedStatesData.features.find(
      (feature) => feature.properties.NAME === stateName
    );
  }, [stateName]);

  const { pathData, viewBox } = useMemo(() => {
    if (!stateFeature) return { pathData: '', viewBox: '0 0 100 100' };

    // Special handling for Alaska
    // Mercator projection tends to distort areas far from the equator.
    // Alaska, being so far north, gets particularly distorted and may appear much larger than expected or be partially cut off.

    if (stateName === 'Alaska') {
      const projection = geoAlbers()
        .rotate([143, 0])
        .center([0, 63.5])
        .parallels([55, 65])
        .scale(Math.min(width, height) * 2.5)
        .translate([width / 2, height / 2]);

      const pathGenerator = geoPath().projection(projection);
      const pathData = pathGenerator(stateFeature as GeoJSON.Feature);

      return {
        pathData,
        viewBox: `0 0 ${width} ${height}`,
      };
    }

    const projection = geoMercator().fitSize(
      [width, height],
      stateFeature as GeoJSON.Feature
    );
    const pathGenerator = geoPath().projection(projection);
    const pathData = pathGenerator(stateFeature as GeoJSON.Feature);

    return {
      pathData,
      viewBox: `0 0 ${width} ${height}`,
    };
  }, [stateFeature, width, height]);

  if (!stateFeature) {
    return <div>{stateName} unavailable</div>;
  }

  return (
    <svg
      width={width}
      height={height}
      viewBox={viewBox}
      style={{ maxWidth: '100%', height: 'auto', background: 'transparent' }}
    >
      <path
        d={pathData ?? ''}
        className={`${fillClassName} ${strokeClassName}`}
        fillOpacity={opacity}
        strokeWidth={strokeWidth}
      />
    </svg>
  );
};

export { StateOutlineMap };
